import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import 'semantic-ui-css/semantic.min.css';
import {
  FileUploader,
  PatientSelector,
  GroupedChartPanel,
  CategoryTabs,
  LandingPage,
  SignUp,
  AuthVerify,
  PrivacyPolicy,
  TermsAndConditions,
  CookiePolicy,
  CookieConsent,
  UserMenu,
  Features,
  About,
  Contact
} from './components';
import {
  fetchPatients
} from './services/api';
import { BloodTestDataProvider, useBloodTestData } from './context/BloodTestDataContext';
import logger from './utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3004';

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [error, setError] = useState(null);

  const loadPatients = async () => {
    try {
      setError(null);
      const data = await fetchPatients();
      logger.log('Loading patients:', data);
      setPatients(data);
      if (data.length > 0 && !selectedPatient) {
        logger.log('Setting first patient as selected:', data[0]);
        setSelectedPatient(data[0]);
      }
    } catch (err) {
      setError('Failed to load patients');
      logger.error('Error loading patients:', err);
    }
  };

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
          credentials: 'include' // Include cookies
        });

        if (response.ok) {
          const data = await response.json();
          logger.log('User is already logged in:', data.user);
          setUser(data.user);
          setIsLoggedIn(true);
        }
      } catch (error) {
        logger.log('Not logged in or session expired');
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    // Only load patients if logged in
    if (isLoggedIn) {
      loadPatients();
    }
  }, [isLoggedIn]);

  const handleFilesUploaded = async (files) => {
    // Refresh patients list after successful upload
    await loadPatients();
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    setUser(null);
    setIsLoggedIn(false);
    setPatients([]);
    setSelectedPatient(null);
  };

  return (
    <Router>
      <CookieConsent />
      <Routes>
        {/* Policy Pages - accessible to everyone */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/cookies" element={<CookiePolicy />} />

        {/* Marketing Pages */}
        <Route path="/features" element={<Features />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />

        {/* Auth Routes */}
        <Route path="/signup" element={<SignUp />} />
        <Route path="/auth/verify" element={<AuthVerify onLogin={handleLogin} />} />

        {/* Main App Routes */}
        <Route path="/" element={
          !isLoggedIn ? (
            <LandingPage onLogin={handleLogin} />
          ) : (
            <BloodTestDataProvider userId={user?.id}>
              <Dashboard
                user={user}
                patients={patients}
                selectedPatient={selectedPatient}
                setSelectedPatient={setSelectedPatient}
                handleFilesUploaded={handleFilesUploaded}
                loading={loading}
                setLoading={setLoading}
                error={error}
                onLogout={handleLogout}
              />
            </BloodTestDataProvider>
          )
        } />
      </Routes>
    </Router>
  );
};

const Dashboard = ({ user, patients, selectedPatient, setSelectedPatient, handleFilesUploaded, loading, setLoading, error, onLogout }) => {
  const [isDragging, setIsDragging] = useState(false);
  const { setSelectedPatientId } = useBloodTestData();

  // Sync selectedPatient from App state to context
  useEffect(() => {
    if (selectedPatient?.id) {
      setSelectedPatientId(selectedPatient.id);
    }
  }, [selectedPatient, setSelectedPatientId]);

  // Handle drag and drop anywhere on the page
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if leaving the document body
    if (e.target === document.body || !e.relatedTarget) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Trigger file upload via the FileUploader component
      const event = new CustomEvent('filesDropped', { detail: files });
      window.dispatchEvent(event);
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50 p-6"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-blue-500 bg-opacity-20 border-4 border-dashed border-blue-500 z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-lg shadow-2xl p-8 text-center">
            <div className="text-6xl mb-4">📄</div>
            <div className="text-2xl font-bold text-gray-900">Drop PDF files here</div>
            <div className="text-gray-600 mt-2">Release to upload blood test results</div>
          </div>
        </div>
      )}

      <div className="max-w-full mx-auto">
        <header className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://blood-results.co.uk/bloodResults.png"
              alt="Blood Results Logo"
              className="h-16 w-16 object-contain"
            />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Blood Test Results Manager
              </h1>
              <p className="text-gray-600">
                Upload PDF blood test results and track your health metrics over time
              </p>
            </div>
          </div>
          <UserMenu user={user} onLogout={onLogout} />
        </header>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Top toolbar with Patient Selector and Upload */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border p-4">
          <div className="flex flex-wrap gap-4 items-end justify-between">
            {patients.length > 0 && (
              <div className="w-80">
                <PatientSelector
                  patients={patients}
                  selectedPatient={selectedPatient}
                  onPatientSelect={setSelectedPatient}
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                Drop your PDF blood test results on this page or
              </span>
              <FileUploader
                onFilesUploaded={handleFilesUploaded}
                loading={loading}
                setLoading={setLoading}
                compact={true}
              />
            </div>
          </div>
        </div>

        {/* Full-width charts */}
        <CategoryTabs />
      </div>
    </div>
  );
};

export default App;